import { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

import {
    useDeleteUserOrganizationRoles,
    useUpdateUserOrganizationRoles,
} from "src/api/client-api/userOrganizationRoles";
import { useGetAllOrganization } from "src/api/client-api/company/useGetAllOrganization";
import { useSnackbar } from "src/utils/useSnackbar";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { byCompanyId, dedupeCompanies } from "src/pages/Settings/Users/OrganizationRolesSection/companyHelpers";
import { OrganizationRolesDialog } from "src/pages/Settings/Users/OrganizationRolesSection/OrganizationRolesDialog";
import { OrganizationRolesTable } from "src/pages/Settings/Users/OrganizationRolesSection/OrganizationRolesTable";
import { OrganizationRoleCompany, RoleDialogState } from "src/pages/Settings/Users/OrganizationRolesSection/types";

export type { OrganizationRoleCompany } from "src/pages/Settings/Users/OrganizationRolesSection/types";

type OrganizationRolesSectionProps = {
    userId: number | undefined;
    companies: OrganizationRoleCompany[];
    roleOptions: string[];
    isRolesLoading?: boolean;
    canManageRoles: boolean;
    isTargetPlatformAdmin: boolean;
    onCompaniesChange?: (companies: OrganizationRoleCompany[]) => void;
};

export const OrganizationRolesSection = (props: OrganizationRolesSectionProps) => {
    const { userId, companies, roleOptions, isRolesLoading, canManageRoles, isTargetPlatformAdmin, onCompaniesChange } =
        props;
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const updateRolesMutation = useUpdateUserOrganizationRoles();
    const deleteRolesMutation = useDeleteUserOrganizationRoles();
    const companySearch = useDelayedSearch({ defaultDelay: 300 });
    const {
        companies: searchedCompanyOptions,
        totalItems: totalCompanyOptions = 0,
        isLoading: isCompaniesLoading,
        isFetching: isCompaniesFetching,
    } = useGetAllOrganization({
        additionalParams: {
            name: companySearch.delayedSearch,
            order: {
                name: "asc",
            },
        },
        itemsPerPage: 50,
        enabled: canManageRoles,
    });

    const [dialogState, setDialogState] = useState<RoleDialogState | null>(null);
    const [companyToRemove, setCompanyToRemove] = useState<OrganizationRoleCompany | null>(null);

    const uniqueCompanies = useMemo(() => dedupeCompanies(companies), [companies]);

    const availableCompanyOptions = useMemo(() => {
        const selectedCompanyIds = new Set(uniqueCompanies.map(byCompanyId));

        return dedupeCompanies(searchedCompanyOptions ?? []).filter((company) => !selectedCompanyIds.has(company.id));
    }, [searchedCompanyOptions, uniqueCompanies]);

    if (userId === undefined) {
        return null;
    }

    const closeDialog = () => setDialogState(null);
    const closeRemoveConfirm = () => {
        if (!deleteRolesMutation.isLoading) {
            setCompanyToRemove(null);
        }
    };

    const openAddDialog = () => {
        if (!canManageRoles) {
            return;
        }

        companySearch.resetSearch();
        setDialogState({ mode: "add", company: null, roles: [] });
    };

    const openEditDialog = (company: OrganizationRoleCompany, roles: string[]) => {
        if (!canManageRoles) {
            return;
        }

        setDialogState({ mode: "edit", company, roles });
    };

    const saveDialog = async (state: RoleDialogState) => {
        if (!canManageRoles || !state.company) {
            return;
        }

        try {
            if (state.roles.length === 0) {
                await deleteRolesMutation.mutateAsync({ userId, organizationId: state.company.id });
            } else {
                await updateRolesMutation.mutateAsync({
                    userId,
                    organizationId: state.company.id,
                    payload: { roles: state.roles },
                });
            }

            const companyWithRoles = {
                ...state.company,
                roles: state.roles,
            };

            onCompaniesChange?.(
                state.mode === "add" ?
                    dedupeCompanies([...uniqueCompanies, companyWithRoles])
                :   uniqueCompanies.map((company) => (company.id === companyWithRoles.id ? companyWithRoles : company)),
            );

            setSnackbarProps({
                message: t("users.organizationRolesSaveSuccess", "Company roles updated successfully"),
                type: "success",
            });
            closeDialog();
        } catch {
            setSnackbarProps({
                message: t("users.organizationRolesSaveError", "Failed to update company roles"),
                type: "error",
            });
        }
    };

    const confirmRemoveCompany = async () => {
        if (!canManageRoles || !companyToRemove) {
            return;
        }

        try {
            if ((companyToRemove.roles ?? []).length > 0) {
                await deleteRolesMutation.mutateAsync({ userId, organizationId: companyToRemove.id });
            }

            onCompaniesChange?.(uniqueCompanies.filter((selectedCompany) => selectedCompany.id !== companyToRemove.id));
            setSnackbarProps({
                message: t("users.organizationRolesRemoveSuccess", "Company roles removed successfully"),
                type: "success",
            });
            setCompanyToRemove(null);
        } catch {
            setSnackbarProps({
                message: t("users.organizationRolesRemoveError", "Failed to remove company roles"),
                type: "error",
            });
        }
    };

    const isDialogSaving = updateRolesMutation.isLoading || deleteRolesMutation.isLoading;

    const hasAssignedEveryCompany =
        companySearch.delayedSearch === "" && totalCompanyOptions > 0 && totalCompanyOptions <= uniqueCompanies.length;

    return (
        <Box>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ marginBottom: "16px" }}
            >
                <Stack spacing={0.5}>
                    <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "text.primary" }}>
                        {t("users.organizationRoles", "Companies & Roles")}{" "}
                        <Typography component="span" variant="small-medium" sx={{ color: "text.secondary" }}>
                            {`${t("users.organizationRolesCount", "({{count}} companies)", {
                                count: uniqueCompanies.length,
                            })}`}
                        </Typography>
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "text.secondary" }}>
                        {t("users.organizationRolesHelper", "Company role changes are saved immediately.")}
                    </Typography>
                </Stack>

                {canManageRoles && (
                    <Tooltip
                        title={
                            hasAssignedEveryCompany ?
                                t("users.organizationRolesNoCompaniesToAdd", "No more companies available")
                            :   ""
                        }
                    >
                        <span>
                            <Button
                                variant="primary"
                                startIcon={<AddIcon />}
                                disabled={hasAssignedEveryCompany || isCompaniesLoading || isRolesLoading}
                                onClick={openAddDialog}
                            >
                                {t("users.organizationRolesAdd", "Add Companies & Roles")}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Stack>

            <OrganizationRolesTable
                companies={uniqueCompanies}
                isRolesLoading={isRolesLoading}
                canManageRoles={canManageRoles}
                onEdit={openEditDialog}
                onRemove={setCompanyToRemove}
            />

            <Dialog
                open={companyToRemove !== null}
                onClose={closeRemoveConfirm}
                maxWidth="sm"
                fullWidth
                aria-labelledby="organization-roles-remove-title"
            >
                <DialogTitle id="organization-roles-remove-title">
                    {t("users.organizationRolesRemoveConfirmTitle", "Remove company roles")}
                </DialogTitle>
                <DialogContent>
                    {isTargetPlatformAdmin && (
                        <Alert severity="warning" role="alert" sx={{ marginBottom: (theme) => theme.spacing(2) }}>
                            {t(
                                "users.organizationRolesPlatformAdminWarning",
                                "This user is a platform administrator. Changing or removing organization roles may affect platform access.",
                            )}
                        </Alert>
                    )}
                    <Typography variant="small-medium" sx={{ color: "text.secondary", lineHeight: "1.6" }}>
                        {t(
                            "users.organizationRolesRemoveConfirmMessage",
                            "Are you sure you want to remove all roles for {{companyName}}? This company will no longer be assigned to this user.",
                            { companyName: companyToRemove?.name ?? "" },
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: (theme) => theme.spacing(2, 3, 3), gap: 1 }}>
                    <Button type="button" variant="secondary" onClick={closeRemoveConfirm} disabled={isDialogSaving}>
                        {t("common.cancel", "Cancel")}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={() => {
                            void confirmRemoveCompany();
                        }}
                        disabled={isDialogSaving}
                        sx={{
                            background: (theme) =>
                                `linear-gradient(${theme.palette.error.dark}, ${theme.palette.error.main})`,
                            "&:hover": {
                                background: (theme) =>
                                    `linear-gradient(${theme.palette.error.dark}, ${theme.palette.error.dark})`,
                            },
                        }}
                    >
                        {isDialogSaving ?
                            <CircularProgress color="inherit" size={16} />
                        :   t("users.organizationRolesRemoveConfirm", "Confirm removal")}
                    </Button>
                </DialogActions>
            </Dialog>

            {dialogState && (
                <OrganizationRolesDialog
                    open
                    state={dialogState}
                    companyOptions={dialogState.mode === "add" ? availableCompanyOptions : uniqueCompanies}
                    companyInputValue={companySearch.search}
                    roleOptions={roleOptions}
                    isCompaniesLoading={isCompaniesFetching}
                    isRolesLoading={isRolesLoading}
                    isSaving={isDialogSaving}
                    showPlatformAdminWarning={isTargetPlatformAdmin}
                    onCompanyInputChange={companySearch.setSearch}
                    onClose={closeDialog}
                    onSave={(state) => {
                        void saveDialog(state);
                    }}
                />
            )}
        </Box>
    );
};
